import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Plus, X } from 'lucide-react';
import type { Passenger } from '../types';
import { springConfig } from '../constants';
import { cn } from '@/lib/utils';

// Format name to title case (first letter uppercase, rest lowercase)
function toTitleCase(str: string): string {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

interface PassengerChipsProps {
  passengers: Passenger[];
  editable?: boolean;
  onAdd?: () => void;
  onRemove?: (id: string) => void;
  onUpdate?: (id: string, field: 'first_name' | 'last_name', value: string) => void;
  compact?: boolean;
}

// Inline editable name field component
interface EditableNameFieldProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  isModified: boolean;
  compact?: boolean;
}

function EditableNameField({
  value,
  placeholder,
  onChange,
  isModified,
  compact = false,
}: EditableNameFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync edit value when external value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(value);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const trimmedValue = editValue.trim();
    if (trimmedValue !== value) {
      onChange(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const isEmpty = !value;
  // Format value to title case for display, keep placeholder as-is
  const displayValue = value ? toTitleCase(value) : placeholder;

  return (
    <span className="relative inline-flex items-center">
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'bg-transparent border-b-2 border-blue-500 outline-none',
            compact ? 'text-xs' : 'text-sm',
            'font-medium text-slate-700 min-w-[60px]'
          )}
          style={{ width: `${Math.max(editValue.length || placeholder.length, 5)}ch` }}
        />
      ) : (
        <span
          onClick={handleClick}
          className={cn(
            'cursor-pointer hover:bg-slate-100 rounded px-1 transition-colors',
            compact ? 'text-xs' : 'text-sm',
            'font-medium',
            isEmpty ? 'text-slate-400 italic' : 'text-slate-700'
          )}
        >
          {displayValue}
        </span>
      )}
      {/* Amber modification indicator */}
      <AnimatePresence>
        {isModified && !isEditing && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-amber-400"
          />
        )}
      </AnimatePresence>
    </span>
  );
}

// Generate avatar colors based on name
function getAvatarColor(name: string): string {
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
}

export function PassengerChips({
  passengers,
  editable = false,
  onAdd,
  onRemove,
  onUpdate,
  compact = false,
}: PassengerChipsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {passengers.map((passenger, index) => {
        // Check if passenger is new (added by user) - track modifications
        const isNew = passenger._isNew === true;
        const isFirstNameModified = isNew && passenger.first_name !== '';
        const isLastNameModified = isNew && passenger.last_name !== '';

        return (
          <motion.div
            key={passenger._id}
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              ...springConfig.default,
              delay: index * 0.05,
            }}
            className={cn(
              'group relative flex items-center gap-2 rounded-full bg-white border',
              isNew ? 'border-amber-200' : 'border-slate-200',
              compact ? 'pl-1 pr-2 py-0.5' : 'pl-1 pr-3 py-1',
              'shadow-sm hover:shadow-md transition-shadow'
            )}
          >
            {/* Avatar */}
            <div
              className={cn(
                'rounded-full flex items-center justify-center text-white font-medium flex-shrink-0',
                getAvatarColor(`${passenger.first_name}${passenger.last_name}`),
                compact ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs'
              )}
            >
              {getInitials(passenger.first_name, passenger.last_name)}
            </div>

            {/* Name - Editable or static based on mode */}
            {editable && onUpdate ? (
              <span className="flex items-center gap-1">
                <EditableNameField
                  value={passenger.first_name}
                  placeholder="Vorname"
                  onChange={(value) => onUpdate(passenger._id, 'first_name', value)}
                  isModified={isFirstNameModified}
                  compact={compact}
                />
                <EditableNameField
                  value={passenger.last_name}
                  placeholder="Nachname"
                  onChange={(value) => onUpdate(passenger._id, 'last_name', value)}
                  isModified={isLastNameModified}
                  compact={compact}
                />
              </span>
            ) : (
              <span
                className={cn(
                  'font-medium text-slate-700',
                  compact ? 'text-xs' : 'text-sm'
                )}
              >
                {toTitleCase(passenger.first_name)} {toTitleCase(passenger.last_name)}
              </span>
            )}

            {/* New badge for newly added passengers */}
            {isNew && editable && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Neu
              </span>
            )}

            {/* Remove button (editable mode) - allow removing new passengers always, or any passenger if more than 1 */}
            {editable && onRemove && (isNew || passengers.length > 1) && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: isNew ? 1 : 0 }}
                whileHover={{ opacity: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(passenger._id);
                }}
                className={cn(
                  'absolute -top-1 -right-1 w-4 h-4 rounded-full',
                  'bg-red-500 text-white flex items-center justify-center',
                  // Always visible for new passengers, hover-only for existing
                  isNew ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  'transition-opacity hover:bg-red-600'
                )}
              >
                <X className="w-2.5 h-2.5" />
              </motion.button>
            )}
          </motion.div>
        );
      })}

      {/* Add button */}
      {editable && onAdd && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onAdd}
          className={cn(
            'flex items-center gap-1.5 rounded-full border-2 border-dashed border-slate-300',
            'text-slate-500 hover:border-blue-400 hover:text-blue-500 transition-colors',
            compact ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'
          )}
        >
          <Plus className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          <span>Hinzufügen</span>
        </motion.button>
      )}

      {/* Empty state */}
      {passengers.length === 0 && !editable && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <User className="w-4 h-4" />
          <span>Keine Passagiere</span>
        </div>
      )}
    </div>
  );
}
